import React, { useCallback, useEffect, useMemo, useState } from "react";
import { observer } from "mobx-react";
import {
  Alert,
  Box,
  Collapse,
  Grid2,
  IconButton,
  Paper,
  useTheme,
} from "@mui/material";
import { ExpandLess, ExpandMore, Refresh } from "@mui/icons-material";
import dayjs, { Dayjs } from "dayjs";
import { useSearchParams } from "react-router-dom";
import { useStores } from "../../../../use-stores";
import { FilterValue } from "../../../../../../models/Filters/FilterValue";
import {
  ALERT_AUDIENCES,
  ALERT_SEVERITIES,
  ALERT_STATUSES,
  AlertAudience,
  AlertsAppliedFilters,
  AlertSeverityCode,
  AlertStatusCode,
  defaultAlertsAppliedFilters,
} from "../../../../../../models/Dashboard/AlertsDashboard/AlertsDashboardFilters";
import { AlertsFilterInfo } from "../../../../../../models/Dashboard/AlertsDashboard/AlertsDashboardSummary";
import { AlertsDashboardStore } from "../../../../stores/dashboards/AlertsDashboardStore";
import CustomTypography from "../../../../../../views/components/common/form/CustomTypography";
import AppButton from "../../../../../../views/components/common/buttons/AppButton";
import AppSearchDropdownMulti from "../../../../../../views/components/common/filter/AppSearchDropdownMulti";
import { DateRangePickerNew } from "../../../../../../views/components/common/textFields/DatePickerRangeNew";
import { useDebounce } from "../../../../../../views/components/common/hooks/Debounce";
import RedemptionHint from "../redemption/RedemptionHint";
import {
  formatAlertsDateTime,
  humanizeAlertsCode,
} from "./alertsDashboardFormat";

const SEARCH_DEBOUNCE_MS = 400;

type DatePreset = "7d" | "30d" | "90d" | "fy";

const DATE_PRESETS: Array<{ id: DatePreset; label: string }> = [
  { id: "7d", label: "7d" },
  { id: "30d", label: "30d" },
  { id: "90d", label: "90d" },
  { id: "fy", label: "FY" },
];

const AUDIENCE_OPTIONS: FilterValue[] = [
  { id: "ALL", value: "All" },
  { id: "ADMIN", value: "Admin" },
  { id: "CUSTOMER", value: "Customer" },
];

const SEVERITY_OPTIONS: FilterValue[] = ALERT_SEVERITIES.map((code) => ({
  id: code,
  value: humanizeAlertsCode(code),
}));

const STATUS_LABELS: Record<AlertStatusCode, string> = {
  OPEN: "Open",
  RESOLVED: "Resolved",
  NONE: "Informational",
};

const STATUS_OPTIONS: FilterValue[] = ALERT_STATUSES.map((code) => ({
  id: code,
  value: STATUS_LABELS[code],
}));

const getErrorMessage = (error: unknown, fallback: string): string => {
  if (error instanceof Error && error.message) {
    return error.message;
  }
  return fallback;
};

const getIndianFyStart = (today: Dayjs): Dayjs => {
  const fyYear = today.month() >= 3 ? today.year() : today.year() - 1;
  return dayjs(`${fyYear}-04-01`).startOf("day");
};

const getPresetRange = (preset: DatePreset): { start: Dayjs; end: Dayjs } => {
  const end = dayjs().endOf("day");
  if (preset === "7d") {
    return { start: dayjs().subtract(6, "day").startOf("day"), end };
  }
  if (preset === "90d") {
    return { start: dayjs().subtract(89, "day").startOf("day"), end };
  }
  if (preset === "fy") {
    return { start: getIndianFyStart(dayjs()), end };
  }
  return { start: dayjs().subtract(29, "day").startOf("day"), end };
};

const matchDatePreset = (start: Dayjs, end: Dayjs): DatePreset | null => {
  const presets: DatePreset[] = ["7d", "30d", "90d", "fy"];
  for (const preset of presets) {
    const range = getPresetRange(preset);
    if (start.isSame(range.start, "day") && end.isSame(range.end, "day")) {
      return preset;
    }
  }
  return null;
};

const parseList = (raw: string | null): string[] =>
  raw
    ? raw
        .split(",")
        .map((item) => item.trim())
        .filter(Boolean)
    : [];

const parseCodes = (raw: string | null): string[] =>
  parseList(raw)
    .map((item) => item.toUpperCase())
    .filter((item) => /^[A-Z0-9_]{1,64}$/.test(item));

const parseIdList = (raw: string | null): number[] =>
  parseList(raw)
    .map((item) => Number(item))
    .filter((id) => Number.isInteger(id) && id > 0);

const parseEnumList = <T extends string>(
  raw: string | null,
  allowed: readonly T[],
): T[] =>
  parseCodes(raw).filter((item): item is T =>
    (allowed as readonly string[]).includes(item),
  );

const parseLocalDay = (raw: string | null, fallback: Dayjs): Dayjs => {
  if (!raw) {
    return fallback;
  }
  const parsed = dayjs(raw);
  return parsed.isValid() ? parsed.startOf("day") : fallback;
};

const parseSearchParamsToApplied = (
  searchParams: URLSearchParams,
): AlertsAppliedFilters => {
  const defaults = defaultAlertsAppliedFilters();
  const audience = searchParams.get("audience");
  return {
    start_date: parseLocalDay(
      searchParams.get("start_date"),
      defaults.start_date,
    ),
    end_date: searchParams.get("end_date")
      ? parseLocalDay(searchParams.get("end_date"), defaults.end_date).endOf(
          "day",
        )
      : defaults.end_date,
    audience: (ALERT_AUDIENCES as readonly string[]).includes(audience || "")
      ? (audience as AlertAudience)
      : null,
    section_codes: parseCodes(searchParams.get("section")),
    type_codes: parseCodes(searchParams.get("type")),
    severities: parseEnumList(searchParams.get("severity"), ALERT_SEVERITIES),
    customer_ids: parseIdList(searchParams.get("customer")),
    statuses: parseEnumList(searchParams.get("status"), ALERT_STATUSES),
  };
};

const appliedToSearchParams = (
  applied: AlertsAppliedFilters,
  current: URLSearchParams,
): URLSearchParams => {
  const params = new URLSearchParams();
  params.set("start_date", applied.start_date.format("YYYY-MM-DD"));
  params.set("end_date", applied.end_date.format("YYYY-MM-DD"));
  if (applied.audience) params.set("audience", applied.audience);
  if (applied.section_codes.length > 0)
    params.set("section", applied.section_codes.join(","));
  if (applied.type_codes.length > 0)
    params.set("type", applied.type_codes.join(","));
  if (applied.severities.length > 0)
    params.set("severity", applied.severities.join(","));
  if (applied.customer_ids.length > 0)
    params.set("customer", applied.customer_ids.join(","));
  if (applied.statuses.length > 0)
    params.set("status", applied.statuses.join(","));
  const tab = current.get("tab");
  if (tab) params.set("tab", tab);
  return params;
};

const mergeSelectedIntoList = (
  list: FilterValue[],
  selected: FilterValue[],
): FilterValue[] => {
  const ids = new Set(list.map((item) => String(item.id)));
  const extras = selected.filter((item) => !ids.has(String(item.id)));
  return extras.length > 0 ? [...extras, ...list] : list;
};

const filterInfoByKeys = (
  items: AlertsFilterInfo[] | undefined,
  keys: string[],
): string | null => {
  if (!items || items.length === 0) {
    return null;
  }
  const match = items.find((item) => keys.includes(item.key));
  return match?.info?.trim() || null;
};

const pickByIds = (
  options: FilterValue[],
  ids: readonly string[],
): FilterValue[] =>
  ids.map(
    (id) =>
      options.find((option) => option.id === id) ?? {
        id,
        value: humanizeAlertsCode(id),
      },
  );

const FilterField = ({
  label,
  hint,
  children,
}: {
  label: string;
  hint?: string | null;
  children: React.ReactNode;
}) => {
  const theme = useTheme();
  return (
    <Box sx={{ width: "100%" }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
        <CustomTypography
          variant="caption"
          fontWeight={600}
          color={theme.palette.customColors.greyTextColor}
        >
          {label}
        </CustomTypography>
        <RedemptionHint text={hint} fontSize={14} />
      </Box>
      <Box sx={{ mt: 0.5 }}>{children}</Box>
    </Box>
  );
};

interface AlertsFiltersProps {
  store: AlertsDashboardStore;
}

const AlertsFilters: React.FC<AlertsFiltersProps> = ({ store }) => {
  const theme = useTheme();
  const colors = theme.palette.customColors;
  const { customerStore } = useStores();
  const [searchParams, setSearchParams] = useSearchParams();

  const initialApplied = useMemo(
    () => parseSearchParamsToApplied(searchParams),
    // Hydrate once from the incoming URL so shared links are not stripped.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  const [isSearchExpanded, setIsSearchExpanded] = useState(true);
  const [isInitialMount, setIsInitialMount] = useState(true);
  const [inlineError, setInlineError] = useState<string | null>(null);
  const [draftStart, setDraftStart] = useState<Dayjs>(
    initialApplied.start_date,
  );
  const [draftEnd, setDraftEnd] = useState<Dayjs>(initialApplied.end_date);
  const [selectedAudience, setSelectedAudience] = useState<FilterValue[]>(
    pickByIds(AUDIENCE_OPTIONS, [initialApplied.audience || "ALL"]),
  );
  const [selectedSections, setSelectedSections] = useState<FilterValue[]>(
    initialApplied.section_codes.map((id) => ({
      id,
      value: humanizeAlertsCode(id),
    })),
  );
  const [selectedTypes, setSelectedTypes] = useState<FilterValue[]>(
    initialApplied.type_codes.map((id) => ({ id, value: id })),
  );
  const [selectedSeverities, setSelectedSeverities] = useState<FilterValue[]>(
    pickByIds(SEVERITY_OPTIONS, initialApplied.severities),
  );
  const [selectedStatuses, setSelectedStatuses] = useState<FilterValue[]>(
    pickByIds(STATUS_OPTIONS, initialApplied.statuses),
  );
  const [selectedCustomers, setSelectedCustomers] = useState<FilterValue[]>(
    initialApplied.customer_ids.map((id) => ({
      id: String(id),
      value: `#${id}`,
    })),
  );
  const [customerSearchResults, setCustomerSearchResults] = useState<
    FilterValue[]
  >([]);
  const [customerQuery, setCustomerQuery] = useState("");
  const [customerSearchOpen, setCustomerSearchOpen] = useState(false);
  const debouncedCustomerQuery = useDebounce(customerQuery, SEARCH_DEBOUNCE_MS);

  const activePreset = matchDatePreset(draftStart, draftEnd);
  const displayedError = inlineError || store.summaryError;
  const meta = store.summaryResult?.meta;
  const filterInfo = meta?.filter_info;
  const hint = (keys: string[]) => filterInfoByKeys(filterInfo, keys);

  // Section / type options come from `summary.by_type.rows` (no filters endpoint).
  const sectionOptions = useMemo(() => {
    const seen = new Map<string, FilterValue>();
    store.knownTypes.forEach((item) => {
      if (!seen.has(item.section_code)) {
        seen.set(item.section_code, {
          id: item.section_code,
          value: item.section_label || humanizeAlertsCode(item.section_code),
        });
      }
    });
    return mergeSelectedIntoList(Array.from(seen.values()), selectedSections);
  }, [store.knownTypes, selectedSections]);

  const typeOptions = useMemo(() => {
    const sectionFilter = new Set(selectedSections.map((item) => item.id));
    const list = store.knownTypes
      .filter(
        (item) =>
          sectionFilter.size === 0 || sectionFilter.has(item.section_code),
      )
      .map((item) => ({
        id: item.type_code,
        value: item.type_code,
        subtitle: `${item.audience === "ADMIN" ? "Admin" : "Customer"} · ${
          item.section_label || item.section_code
        }`,
      }));
    return mergeSelectedIntoList(list, selectedTypes);
  }, [store.knownTypes, selectedSections, selectedTypes]);

  const customerOptions = useMemo(
    () => mergeSelectedIntoList(customerSearchResults, selectedCustomers),
    [customerSearchResults, selectedCustomers],
  );

  // Fill in names for customers hydrated from the URL once by_customer has them.
  useEffect(() => {
    setSelectedCustomers((current) => {
      let changed = false;
      const next = current.map((item) => {
        const name = store.knownCustomers[item.id];
        if (name && item.value !== name) {
          changed = true;
          return { ...item, value: name };
        }
        return item;
      });
      return changed ? next : current;
    });
  }, [store.knownCustomers]);

  const appliedUrlKey = [
    store.appliedFilters.start_date.format("YYYY-MM-DD"),
    store.appliedFilters.end_date.format("YYYY-MM-DD"),
    store.appliedFilters.audience || "",
    store.appliedFilters.section_codes.join(","),
    store.appliedFilters.type_codes.join(","),
    store.appliedFilters.severities.join(","),
    store.appliedFilters.customer_ids.join(","),
    store.appliedFilters.statuses.join(","),
  ].join("|");

  const applyFilters = useCallback(
    async (applied: AlertsAppliedFilters) => {
      try {
        setInlineError(null);
        store.setAppliedFilters(applied);
        await store.refreshDashboard(applied);
      } catch (error) {
        setInlineError(
          store.summaryError ||
            getErrorMessage(error, "Failed to load alerts dashboard."),
        );
      }
    },
    [store],
  );

  const fetchCustomers = useCallback(
    async (searchText: string) => {
      try {
        await customerStore.searchCustomers({
          page_no: 1,
          limit: 20,
          search_text: searchText,
        });
        setCustomerSearchResults(
          (customerStore.customers ?? [])
            .filter((customer) => customer.id != null)
            .map((customer) => ({
              id: String(customer.id),
              value: customer.name || String(customer.id),
            })),
        );
      } catch (error) {
        setCustomerSearchResults([]);
        setInlineError(getErrorMessage(error, "Failed to search customers."));
      }
    },
    [customerStore],
  );

  useEffect(() => {
    if (!customerSearchOpen) {
      return;
    }
    void fetchCustomers(debouncedCustomerQuery.trim());
  }, [customerSearchOpen, debouncedCustomerQuery, fetchCustomers]);

  useEffect(() => {
    const init = async () => {
      try {
        setInlineError(null);
        store.setAppliedFilters(initialApplied);
        store.track("dashboard_view");
        await store.refreshDashboard(initialApplied);
      } catch (error) {
        setInlineError(
          store.summaryError ||
            getErrorMessage(error, "Failed to load alerts dashboard."),
        );
      }
    };
    void init();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (isInitialMount) {
      setIsInitialMount(false);
      return;
    }
    try {
      setSearchParams(
        (current) => appliedToSearchParams(store.appliedFilters, current),
        {
          replace: true,
        },
      );
    } catch (error) {
      setInlineError(
        getErrorMessage(error, "Failed to update the filter URL."),
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appliedUrlKey, setSearchParams]);

  const buildAppliedFromDraft = (): AlertsAppliedFilters => {
    const audience = selectedAudience[0]?.id;
    return {
      start_date: draftStart.startOf("day"),
      end_date: draftEnd.endOf("day"),
      audience:
        audience === "ADMIN" || audience === "CUSTOMER" ? audience : null,
      section_codes: selectedSections.map((item) => item.id),
      type_codes: selectedTypes.map((item) => item.id),
      severities: selectedSeverities.map(
        (item) => item.id as AlertSeverityCode,
      ),
      customer_ids: selectedCustomers
        .map((item) => Number(item.id))
        .filter((id) => Number.isInteger(id) && id > 0),
      statuses: selectedStatuses.map((item) => item.id as AlertStatusCode),
    };
  };

  const handleApply = () => {
    try {
      if (draftStart.isAfter(draftEnd, "day")) {
        setInlineError("Start date must be on or before the end date.");
        return;
      }
      const applied = buildAppliedFromDraft();
      store.setAppliedFilters(applied);
      store.track("filter_apply");
      void applyFilters(applied);
    } catch (error) {
      setInlineError(getErrorMessage(error, "Failed to apply filters."));
    }
  };

  const handleClear = () => {
    try {
      const cleared = defaultAlertsAppliedFilters();
      setDraftStart(cleared.start_date);
      setDraftEnd(cleared.end_date);
      setSelectedAudience(pickByIds(AUDIENCE_OPTIONS, ["ALL"]));
      setSelectedSections([]);
      setSelectedTypes([]);
      setSelectedSeverities([]);
      setSelectedStatuses([]);
      setSelectedCustomers([]);
      setInlineError(null);
      store.setAppliedFilters(cleared);
      store.track("filter_clear");
      void applyFilters(cleared);
    } catch (error) {
      setInlineError(getErrorMessage(error, "Failed to reset filters."));
    }
  };

  const handlePresetClick = (preset: DatePreset) => {
    try {
      const range = getPresetRange(preset);
      setDraftStart(range.start);
      setDraftEnd(range.end);
      store.track("date_preset_select", { preset });
    } catch (error) {
      setInlineError(getErrorMessage(error, "Failed to set the date preset."));
    }
  };

  const handleRangeSelect = (range: {
    startDate: Dayjs | null;
    endDate: Dayjs | null;
  }) => {
    try {
      if (range.startDate && range.endDate) {
        const start = range.startDate.startOf("day");
        const end = range.endDate.endOf("day");
        setDraftStart(start);
        setDraftEnd(end);
        store.track("date_range_change", {
          context: {
            draft_start_date: start.format("YYYY-MM-DD"),
            draft_end_date: end.format("YYYY-MM-DD"),
          },
        });
      }
    } catch (error) {
      setInlineError(
        getErrorMessage(error, "Failed to set the custom date range."),
      );
    }
  };

  const handleRefresh = async () => {
    try {
      setInlineError(null);
      await store.refreshData();
    } catch (error) {
      setInlineError(
        store.summaryError ||
          getErrorMessage(error, "Failed to refresh the alerts dashboard."),
      );
    }
  };

  const dropdownCell = { xs: 12, sm: 6, md: 4, lg: 3 } as const;

  return (
    <Paper
      sx={{
        mt: 3,
        p: { xs: 2, md: 3 },
        borderRadius: 2,
        boxShadow:
          "rgba(0, 0, 0, 0.15) 0px 4px 24px, rgba(0, 0, 0, 0.05) 0px 8px 32px",
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
          gap: 2,
          flexWrap: "wrap",
        }}
      >
        <Box>
          <CustomTypography variant="heading3" color={colors.darkBlueText}>
            Alerts monitoring
          </CustomTypography>
          <CustomTypography variant="body2" color={colors.greyTextColor}>
            Alerts raised in this window, who received and read them, and
            whether admin push and client email deliveries went out.
          </CustomTypography>
          {meta?.generated_at ? (
            <CustomTypography
              variant="body4"
              color={colors.greyTextColor}
              sx={{ mt: 0.5 }}
            >
              Updated {formatAlertsDateTime(meta.generated_at)} · numbers can be
              up to 5 minutes old
            </CustomTypography>
          ) : null}
        </Box>
        <AppButton
          label={store.summaryLoading ? "Refreshing..." : "Refresh"}
          variant="secondary"
          startIcon={<Refresh />}
          onClick={() => void handleRefresh()}
          disabled={store.summaryLoading}
          customStyles={{ minHeight: 36, height: 36, px: 2 }}
        />
      </Box>

      <Paper
        variant="outlined"
        sx={{
          mt: 2,
          borderRadius: 2,
          borderColor: "divider",
          overflow: "hidden",
        }}
      >
        <Box
          onClick={() => {
            setIsSearchExpanded((prev) => {
              const next = !prev;
              store.track("search_panel_toggle", { expanded: next });
              return next;
            });
          }}
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            px: 2,
            py: 1.25,
            cursor: "pointer",
          }}
        >
          <CustomTypography variant="heading6" fontWeight={600}>
            Search
            {meta && meta.filters_applied > 0
              ? ` · ${meta.filters_applied} applied`
              : ""}
          </CustomTypography>
          <IconButton
            size="small"
            aria-label={isSearchExpanded ? "Collapse search" : "Expand search"}
          >
            {isSearchExpanded ? <ExpandLess /> : <ExpandMore />}
          </IconButton>
        </Box>
        <Collapse in={isSearchExpanded}>
          <Box sx={{ px: 2, pb: 2 }}>
            <Grid2 container spacing={2} columns={12}>
              <Grid2 size={{ xs: 12 }}>
                <FilterField
                  label="Date range"
                  hint={hint(["date", "date_range", "start_date"])}
                >
                  <Box
                    sx={{
                      display: "flex",
                      flexWrap: "wrap",
                      gap: 1,
                      alignItems: "center",
                    }}
                  >
                    {DATE_PRESETS.map((preset) => (
                      <AppButton
                        key={preset.id}
                        label={preset.label}
                        variant={
                          activePreset === preset.id ? "primary" : "grayscale"
                        }
                        onClick={() => handlePresetClick(preset.id)}
                        customStyles={{
                          minHeight: 32,
                          height: 32,
                          minWidth: "auto",
                          px: 1.5,
                        }}
                      />
                    ))}
                    <Box sx={{ flex: 1 }}>
                      <DateRangePickerNew
                        startDate={draftStart}
                        endDate={draftEnd}
                        onRangeSelect={handleRangeSelect}
                      />
                    </Box>
                  </Box>
                </FilterField>
              </Grid2>
              <Grid2 size={dropdownCell}>
                <FilterField label="Audience" hint={hint(["audience"])}>
                  <AppSearchDropdownMulti
                    title="Audience"
                    isMultiSelect={false}
                    list={AUDIENCE_OPTIONS}
                    value={selectedAudience}
                    local
                    floatingLabel
                    handleSelect={(items) => setSelectedAudience(items)}
                  />
                </FilterField>
              </Grid2>
              <Grid2 size={dropdownCell}>
                <FilterField
                  label="Section"
                  hint={hint(["section", "section_codes"])}
                >
                  <AppSearchDropdownMulti
                    title="Section"
                    isMultiSelect
                    list={sectionOptions}
                    value={selectedSections}
                    local
                    floatingLabel
                    handleSelect={(items) => setSelectedSections(items)}
                  />
                </FilterField>
              </Grid2>
              <Grid2 size={dropdownCell}>
                <FilterField label="Type" hint={hint(["type", "type_codes"])}>
                  <AppSearchDropdownMulti
                    title="Type"
                    isMultiSelect
                    list={typeOptions}
                    value={selectedTypes}
                    local
                    floatingLabel
                    handleSelect={(items) => setSelectedTypes(items)}
                  />
                </FilterField>
              </Grid2>
              <Grid2 size={dropdownCell}>
                <FilterField
                  label="Severity"
                  hint={hint(["severity", "severities"])}
                >
                  <AppSearchDropdownMulti
                    title="Severity"
                    isMultiSelect
                    list={SEVERITY_OPTIONS}
                    value={selectedSeverities}
                    local
                    floatingLabel
                    handleSelect={(items) => setSelectedSeverities(items)}
                  />
                </FilterField>
              </Grid2>
              <Grid2 size={dropdownCell}>
                <FilterField
                  label="Customer"
                  hint={hint(["customer", "customer_ids"])}
                >
                  <AppSearchDropdownMulti
                    title="Customer"
                    isMultiSelect
                    list={customerOptions}
                    value={selectedCustomers}
                    local={false}
                    floatingLabel
                    handleSearch={(searchText) =>
                      setCustomerQuery(searchText ?? "")
                    }
                    handleSelect={(items) => setSelectedCustomers(items)}
                    onOpen={() => setCustomerSearchOpen(true)}
                  />
                </FilterField>
              </Grid2>
              <Grid2 size={dropdownCell}>
                <FilterField
                  label="Alert status"
                  hint={hint(["status", "statuses"])}
                >
                  <AppSearchDropdownMulti
                    title="Alert status"
                    isMultiSelect
                    list={STATUS_OPTIONS}
                    value={selectedStatuses}
                    local
                    floatingLabel
                    handleSelect={(items) => setSelectedStatuses(items)}
                  />
                </FilterField>
              </Grid2>
              <Grid2 size={dropdownCell} />
              <Grid2
                size={dropdownCell}
                sx={{
                  display: "flex",
                  flexDirection: "row",
                  alignItems: "flex-end",
                  gap: 2,
                }}
              >
                <AppButton
                  variant="transparentPrimary"
                  label="Clear"
                  onClick={handleClear}
                  disabled={store.summaryLoading}
                  customStyles={{ width: "100%" }}
                />
                <AppButton
                  variant="primary"
                  label="Apply"
                  onClick={handleApply}
                  disabled={store.summaryLoading}
                  customStyles={{ width: "100%" }}
                />
              </Grid2>
            </Grid2>
          </Box>
        </Collapse>
      </Paper>

      {displayedError ? (
        <Alert severity="error" sx={{ mt: 2 }}>
          {displayedError}
        </Alert>
      ) : null}
    </Paper>
  );
};

export default observer(AlertsFilters);
